import React, { useEffect, useState } from 'react';
import { Modal, Container, Row, Col, Card } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import logo from '../../../images/MyGymTV-white.png';
import TotalDisplaysIcon from '../../../images/mobilereport/MOBILE_EMAIL_ICONS_TOTAL_IMPRESSIONS.png';
import '../../../App.css';

// Helper function to preload images
const preloadImage = (url) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = 'image';
    document.head.appendChild(link);
};

const OverheadCampaignReport = () => {
    const { initialLoginData } = useSelector((state) => state.user);
    const agreements = initialLoginData?.Agreements || [];
    const [selectedContract, setSelectedContract] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const userName = initialLoginData?.Account?.ContactName?.split(' ')[0];

    // For overhead reports, assume a contract is valid if it has at least one overhead URL.
    const overheadContracts = agreements.flatMap((agreement) =>
        agreement.Contracts.filter(
            (contract) => contract.LocalMockupUrlNS || contract.LocalMockupUrlSS
        )
    );

    const groupedContracts = overheadContracts.reduce((acc, contract) => {
        const monthEnding = contract.MobileCampaignUpdateMonthEnding || 'N/A';
        if (!acc[monthEnding]) {
            acc[monthEnding] = [];
        }
        acc[monthEnding].push(contract);
        return acc;
    }, {});

    // Modal handlers
    const handleOpenModal = (contract) => {
        setSelectedContract(contract);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedContract(null);
    };

    // Preload images when the component mounts
    useEffect(() => {
        const imagesToPreload = [
            logo,
            TotalDisplaysIcon
        ];
        imagesToPreload.forEach(preloadImage);
    }, []);

    return (
        <div>
            <Container className="my-3 g-0">
                {Object.keys(groupedContracts).map((monthEnding, index) => (
                    <Card
                        className="m-3 col-lg-8 col-md-10 col-sm-12 mx-auto shadow-sm border-0"
                        key={index}
                        style={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                        }}
                    >
                        <Card.Header
                            className="d-flex justify-content-between align-items-center"
                            style={{
                                background: 'linear-gradient(135deg, #026ac6, #39aae2)',
                                color: 'white',
                                fontWeight: '600',
                            }}
                        >
                            <div>
                                Locations
                            </div>
                            <FontAwesomeIcon
                                className="text-white"
                                icon={faCircleInfo}
                            />
                        </Card.Header>
                        <Card.Body
                            className="p-3"
                            style={{
                                backgroundColor: '#f8f9fa',
                            }}
                        >
                            {groupedContracts[monthEnding].map((contract, idx) => (
                                <div
                                    className="mb-3 p-2 bg-white rounded shadow-sm"
                                    key={idx}
                                    onClick={() => handleOpenModal(contract)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-3px)';
                                        e.currentTarget.style.boxShadow =
                                            '0 8px 15px rgba(0, 0, 0, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                    }}
                                >
                                    <div className="row align-items-center">
                                        <div className="col-12">
                                            <strong className="text-dark">{contract.Venue}</strong>
                                        </div>
                                        <div className="col-12 text-start text-secondary">
                                            <small>{contract.ContractNumber}</small>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                ))}
            </Container>

            <Modal
                centered
                onHide={handleCloseModal}
                show={modalOpen}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="h5 w-100">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="me-4">In-Gym Campaign Report</div>
                            <img
                                alt="In-Gym Campaign Header"
                                className="overhead-campaign-report-logo"
                                src={logo}
                            />
                        </div>
                    </Modal.Title>

                </Modal.Header>
                {selectedContract && (
                    <Modal.Body>
                        {/* Introduction */}
                        <div className="mb-4 px-3">
                            <p>Hello {userName},</p>
                            <p>
                                Thank you for partnering with us on your in-gym advertising campaign.
                                Our campaigns are designed to maximize your brand’s visibility in high-traffic areas.
                            </p>
                        </div>

                        {/* Ad Preview Section */}
                        <div className="mb-4 px-3 text-center">
                            <h6 className="fw-bold">Ad Preview</h6>
                            {selectedContract.LocalMockupUrlSS ? (
                                <video
                                    className="img-fluid mx-auto"
                                    controls
                                    style={{ maxHeight: '300px' }}
                                >
                                    <source
                                        src={`${selectedContract.LocalMockupUrlSS.Url}#t=1`}
                                        type="video/mp4"
                                    />
                                    Your browser does not support the video tag.
                                </video>
                            ) : selectedContract.LocalMockupUrlNS ? (
                                <img
                                    alt="Ad Preview"
                                    className="img-fluid mx-auto"
                                    src={selectedContract.LocalMockupUrlNS.Url}
                                />
                            ) : (
                                <p className="text-muted">No preview available.</p>
                            )}
                        </div>

                        {/* Metrics Section */}
                        <Container className="mb-4">
                            <Row className="justify-content-center text-center g-3">
                                <Col
                                    md={4}
                                    xs={12}
                                >
                                    {
                                        selectedContract.OverheadImpressions > 0 &&
                                        <div>
                                            <img
                                                alt="Total Displays Icon"
                                                className="metrics-icon img-fluid mb-2"
                                                src={TotalDisplaysIcon}
                                                style={{ maxWidth: '80px' }}
                                            />
                                            <h5>{selectedContract.OverheadImpressions ?
                                                Number(selectedContract.OverheadImpressions).toLocaleString() : '0'}</h5>
                                            <p className="metrics-title fw-bold">Total Impressions</p>
                                            <p className="metrics-description">
                                                Shows the impressions generated to date for your campaign.
                                            </p>
                                        </div>
                                    }

                                </Col>                               
                            </Row>
                        </Container>

                        {/* Additional Details Section */}
                        <div className="px-3">
                            <p>
                                <strong>Venue Name:</strong> {selectedContract.Venue || 'N/A'}
                            </p>
                            <p>
                                <strong>Contract Number:</strong> {selectedContract.ContractNumber || 'N/A'}
                            </p>
                            <p>
                                <strong>Date Signed:</strong>{' '}
                                {selectedContract.DateSigned
                                    ? new Date(selectedContract.DateSigned).toLocaleDateString('en-US')
                                    : 'N/A'}
                            </p>
                            <p>
                                <strong>Initial Broadcast Date:</strong>{' '}
                                {selectedContract.TargetBroadcastDate
                                    ? new Date(selectedContract.TargetBroadcastDate).toLocaleDateString('en-US')
                                    : 'N/A'}
                            </p>
                            {selectedContract.Advertiser && (
                                <p>
                                    <strong>Advertiser:</strong> {selectedContract.Advertiser}
                                </p>
                            )}
                        </div>
                    </Modal.Body>
                )}
            </Modal>
        </div>
    );
};

export default OverheadCampaignReport;
